/* eslint-disable prefer-rest-params */
/**
 * Import OL classes
 */
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';
import Style from 'ol/style/Style';
import {getWidth as getExtentWidth, getHeight as getExtentHeight, getTopLeft, getCenter as getExtentCenter, getTopRight, getBottomLeft, getBottomRight} from 'ol/extent';
import {DEVICE_PIXEL_RATIO as hasDevicePixelRatio} from 'ol/has';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point';
import {fromExtent} from 'ol/geom/Polygon';
import Circle from 'ol/geom/Circle';

/**
 * Import own classes
 */
import {replaceTextPlaceholder} from '/src/modules/language/language';
import {hexToRgbA} from '/src/modules/misc/misc';
import {createPseudoPolygon, stylePolygon} from '/src/modules/features/polygon';

/**
 * Import global variables
 */
import {exhibitionConfig, map} from '/main';
import {styleLineString} from '/src/modules/features/lineString';
import {stylePoint} from '/src/modules/features/point';
import { showOnlyNischenstaende } from '../../../main';
import Feature from 'ol/Feature';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';

export let dividerInt = 20;
export let fontSize = 12;

/**
 * Function for styling given features
 * @param {object} feature
 * @param {number} resolution
 * @param {Boolean} selected
 * @return {*}
 */
export function featureStyleCall(feature, resolution, selected = false) {
  const featureStyle = defineFeatureStyle(feature);

  if (selected === true) {
    return staendeStyle(feature, resolution);
  }

  if (typeof featureStyle['gradient'] !== 'undefined' && featureStyle['gradient'] !== null) {
    return drawGradient(feature, resolution);
  } else {
    if (typeof featureStyle['text'] !== 'undefined' && featureStyle['text'] !== null) {
      if (featureStyle['text']['maxResolution'] < resolution) {
        fontSize = 0;
      } else {
        fontSize = 12;
      }
    }

    switch (feature.getGeometry().getType()) {
      case 'Polygon': return stylePolygon(feature, resolution, selected);
      case 'LineString': return styleLineString(feature, resolution, selected);
      case 'Point': return stylePoint(feature, resolution, selected);
      default: return new Style();
    }
  }
}

/**
 * Function to create a gradient style for chosen features
 * @param {object} feature - The feature to be styled
 * @param {object} resolution - The current resolution
 * @return {*}
 */
function drawGradient(feature, resolution) {
  let fontSize = 12;
  let style;
  let featureStyle = feature.get('style');

  if (featureStyle['text'] !== null) {
    if (featureStyle['text']['maxResolution'] < resolution) {
      fontSize = 0;
    }
  }

  /**
   * @param {object} feature
   * @param {object} resolution
   * @return {CanvasGradient}
   */
  function gradient(feature, resolution) {
    let canvas = document.createElement('canvas');
    let context = canvas.getContext('2d', {willReadFrequently: true});
    let pixelRatio = hasDevicePixelRatio;
    let featureExtent = feature.getGeometry().getExtent();
    let grad = context.createLinearGradient(0, 0, 0, getExtentWidth(featureExtent) / resolution * pixelRatio);

    grad.addColorStop(0, hexToRgbA(featureStyle['gradient']['gradientColor1'], featureStyle['gradient']['gradientOpacity']));
    grad.addColorStop(1, hexToRgbA(featureStyle['gradient']['gradientColor2'], featureStyle['gradient']['gradientOpacity']));

    return grad;
  }

  if (feature.getGeometry().getType() === 'Polygon') {
    let text = '';

    if (typeof feature.get('name') !== 'undefined') {
      let featureName = feature.get('name');

      featureName = replaceTextPlaceholder(featureName);

      text = stringDivider(featureName, dividerInt, '\n');
      // text = wordWrap(featureName, dividerInt);
    } else {
      text = '';
    }

    style = new Style({
      fill: new Fill({
        color: gradient(feature, resolution),
      }),
      stroke: new Stroke({
        color: featureStyle['general']['stroke'],
        width: featureStyle['general']['strokeWidth'],
        opacity: featureStyle['general']['strokeOpacity'],
      }),
    });

    if (featureStyle['text']['maxResolution'] > resolution) {
      let textObject = new Text({
        font: fontSize + 'px sans-serif',
        fill: new Fill({
          color: featureStyle['text']['color'],
        }),
        stroke: new Stroke({
          color: featureStyle['text']['stroke'],
          width: featureStyle['text']['strokeWidth'],
          opacity: featureStyle['text']['strokeOpacity'],
        }),
        text: text,
        overflow: false,
      });
      style.setText(textObject);
    }

    return style;
  }
}

/**
 * @param {object} feature
 * @param {object} resolution
 * @return {*}
 */
function staendeStyle(feature, resolution) {
  let text;
  let fontSize = 12;
  let textObject;
  let style;
  let numberInCenter = false;
  let textStyle;
  let numberObject;
  let featureStyle = exhibitionConfig['styles']['booths'];
  let featureNumber = typeof feature.get('number') !== 'undefined' ? feature.get('number') : null;
  let styleArray = [];
  const featureMainExhibitor = feature.get('mainExhibitor');
  const isNischenstand = feature.get('isNischenstand') ?? false;
  const isSold = feature.get('isSold') ?? false;

  const hasValue = (value) => value !== undefined && value !== null && value !== '';
  const mainName = featureMainExhibitor?.[0]?.name ?? null;
  let middleText = '';
  // let nischenText = '';

  if (showOnlyNischenstaende === true) {
    if (isNischenstand && isSold) {
        // nischenText = stringDivider(lang['SOLD'], dividerInt, '\n');

        if (mainName !== null && featureMainExhibitor?.length > 0) {
          middleText = stringDivider(mainName, dividerInt, '\n');
        } /* else {
          middleText = nischenText;
        } */
      } else if (isNischenstand && !isSold) {
        // nischenText = stringDivider(lang['FREE'], dividerInt, '\n');

        if (mainName !== null && featureMainExhibitor?.length > 0) {
          middleText = stringDivider(mainName, dividerInt, '\n');
        } /* else {
          middleText = nischenText;
        } */
      } else {
      if (mainName !== null && featureMainExhibitor?.length > 0) {
        middleText = stringDivider(mainName, dividerInt, '\n');
      } else if (hasValue(featureNumber)) {
        middleText = ' ' + featureNumber;
        numberInCenter = true;
      } else {
        middleText = '';
      }

      middleText = fitText(feature, middleText, resolution);
    }
  } else {
    if (mainName !== null && featureMainExhibitor?.length > 0) {
      middleText = stringDivider(mainName, dividerInt, '\n');
    } else if (hasValue(featureNumber)) {
      middleText = ' ' + featureNumber;
      numberInCenter = true;
    } else {
      middleText = '';
    }
  }

  textObject = new Text({
    font: fontSize + 'px sans-serif',
    fill: new Fill({
      color: featureStyle['text']['selected']['color'],
    }),
    text: middleText,
    overflow: false,
    rotateWithView: (typeof exhibitionConfig['core']['fairground'] !== 'undefined' && exhibitionConfig['core']['fairground'] == 'koeln') ? false : true,
  });

  style = new Style({
    fill: new Fill({
      color: hexToRgbA(featureStyle['general']['selected']['fill'], featureStyle['general']['selected']['fillOpacity']),
    }),
    stroke: new Stroke({
      color: featureStyle['general']['selected']['stroke'],
      width: featureStyle['general']['selected']['strokeWidth'],
    }),
  });

  /**
   * If the text needs to rotate, calculate a new pseudo feature to match the extent for the rotation.
   */
  if (typeof exhibitionConfig['core']['fairground'] !== 'undefined' && exhibitionConfig['core']['fairground'] == 'koeln') {
    textStyle = new Style({
      geometry: function(feature) {
        return koelnPseudoPolygon(feature, map.getView().getRotation());
      },
    });
  } else {
    let rotateText = true;
    map.getLayers().forEach(function(layer) {
      if (layer.get('name') == 'Freigelände Nord' || layer.get('name') == 'Freigelände 3-8' || layer.get('name') == 'Freigelände 4-8' || layer.get('name') == 'Freigelände 9-13' || layer.get('name') == 'Atrium') {
        rotateText = false;
      }
    });

    if (rotateText == true) {
      textObject.setRotation(90 * (Math.PI/180));
    }
    textStyle = new Style({
      geometry: function(feature) {
        return createPseudoPolygon(feature, true);
      },
      // stroke: new Stroke({
      //   color: '#000000',
      // }),
      // fill: new Fill({
      //   color: 'red',
      // }),
    });
  }
  textStyle.setText(textObject);

  styleArray.push(textStyle);

  if (typeof exhibitionConfig['core']['fairground'] === 'undefined' && exhibitionConfig['core']['fairground'] != 'koeln') {
    if (feature.getGeometry().getCoordinates(false)[0].length > 5) {
      textStyle = new Style({
        geometry: function(feature) {
          return createPseudoPolygon(feature, false);
        },
        // stroke: new Stroke({
        //   color: '#000000',
        // }),
        // fill: new Fill({
        //   color: 'red',
        // }),
      });
      textStyle.setText(textObject);
      styleArray.push(textStyle);
    } else {
      style.setText(textObject);
    }
  }

  if (numberInCenter === false) {
    if (typeof featureNumber !== 'undefined' && featureNumber !== null && featureNumber !== '') {
      // if (feature.getGeometry().getCoordinates()[0].length == 4) {
      //   numberObject = createBoothNumberTextPoint(feature, featureNumber, fontSize, featureStyle, true);
      // } else {
        numberObject = createBoothNumberTextPoint(feature, featureNumber, fontSize, featureStyle, true);
      // }

      if (featureStyle['number']['selected']['backgroundFill'] !== null) {
        numberObject.getText().setBackgroundFill(featureStyle['number']['selected']['backgroundFill']);
      }

      styleArray.push(numberObject);
    }
  }

  if ((typeof feature.get('width') !== 'undefined' && feature.get('width') > 0) && (typeof feature.get('depth') !== 'undefined' && feature.get('depth') > 0)) {
    let sides = calculateSidesForBoothDimension(feature);
    let dimensions;

    if (sides !== undefined) {
      let shortestSide = sides[0];
      let longestSide = sides[1];

      dimensions = createBoothDimensionText(feature, shortestSide, longestSide, true);
    } else {
      dimensions = createBoothDimensionText(feature, false, false, true);
    }

    styleArray.push(dimensions[0]);
    styleArray.push(dimensions[1]);
  }

  styleArray.push(style);

  return styleArray;
}

/**
 * @param {object} feature
 * @param {object} resolution
 * @return {*}
 */
export function alternativeStaendeStyle(feature, resolution) {
  let text;
  let fontSize = 12;
  let textObject;
  let style;
  let numberInCenter = false;
  let textStyle;
  let numberObject;
  let featureStyle = exhibitionConfig['styles']['booths'];
  let featureNumber = typeof feature.get('number') !== 'undefined' ? feature.get('number') : null;
  let styleArray = [];

  if (typeof feature.get('mainExhibitor') !== 'undefined' && feature.get('mainExhibitor').length > 0) {
    if (feature.get('mainExhibitor')['name'] !== null) {
      text = stringDivider(feature.get('mainExhibitor')[0]['name'], dividerInt, '\n');
      // text = wordWrap(feature.get('mainExhibitor')[0]['name'], dividerInt);
    } else {
      if (typeof featureNumber !== 'undefined' && featureNumber !== null && featureNumber !== '') {
        text = ' ' + featureNumber;
        numberInCenter = true;
      } else {
        text = '';
      }
    }
  } else {
    if (typeof featureNumber !== 'undefined' && featureNumber !== null && featureNumber !== '') {
      text = ' ' + featureNumber;
      numberInCenter = true;
    } else {
      text = '';
    }
  }

  textObject = new Text({
    font: fontSize + 'px sans-serif',
    fill: new Fill({
      color: '#666666',
    }),
    text: text,
    overflow: false,
    rotateWithView: true,
  });

  style = new Style({
    fill: new Fill({
      color: '#CCCCCC',
    }),
    stroke: new Stroke({
      color: featureStyle['general']['selected']['strokeColor'],
      width: featureStyle['general']['selected']['strokeWidth'],
    }),
  });

  /**
   * If the text needs to rotate, calculate a new pseudo feature to match the extent for the rotation.
   */

  if (getExtentWidth(feature.getGeometry().getExtent()) < getExtentHeight(feature.getGeometry().getExtent())) {
    if (typeof exhibitionConfig['core']['fairground'] !== 'undefined' && exhibitionConfig['core']['fairground'] == 'koeln') {
      textStyle = new Style({
        geometry: function(feature) {
          return createPseudoPolygon(feature, true);
        },
      });
    }
    textStyle.setText(textObject);

    styleArray.push(textStyle);
    } else {
      textObject.setRotation(90 * (Math.PI/180));
      textStyle = new Style({
        geometry: function(feature) {
          return createPseudoPolygon(feature, true);
        },
      });
    }
    textStyle.setText(textObject);

    styleArray.push(textStyle);

  if (numberInCenter === false) {
    if (typeof featureNumber !== 'undefined' && featureNumber !== null && featureNumber !== '') {
      numberObject = new Style({
        text: new Text({
          text: featureNumber !== null ? ' ' + featureNumber : '',
          font: fontSize - 1 + 'px sans-serif',
          fill: new Fill({
            color: featureStyle['number']['color'],
          }),
          placement: 'line',
          textAlign: 'left',
          textBaseline: 'middle',
          rotateWithView: false,
          overflow: false,
        }),
        geometry: function(feature) {
          if (typeof featureStyle['number']['position'] !== 'undefined' && featureStyle['number']['position'] !== null) {
            return getStandnummerLine(feature, featureStyle['number']['position']);
          } else {
            return getStandnummerLine(feature);
          }
        },
      });

      if (featureStyle['number']['selected']['backgroundFill'] !== null) {
        numberObject.getText().setBackgroundFill(featureStyle['number']['selected']['backgroundFill']);
      }

      styleArray.push(numberObject);
    }
  }

  if ((typeof feature.get('width') !== 'undefined' && feature.get('width') > 0) && (typeof feature.get('depth') !== 'undefined' && feature.get('depth') > 0)) {
    let sides = calculateSidesForBoothDimension(feature);
    let dimensions;

    if (sides !== undefined) {
      let shortestSide = sides[0];
      let longestSide = sides[1];

      dimensions = createBoothDimensionText(feature, shortestSide, longestSide, true);
    } else {
      dimensions = createBoothDimensionText(feature, false, false, true);
    }

    styleArray.push(dimensions[0]);
    styleArray.push(dimensions[1]);
  }

  styleArray.push(style);

  return styleArray;
}

/**
 * Function to divide long strings (adds line-breaks for text)
 * @param {string} str
 * @param {int} width
 * @param {string} spaceReplacer
 * @return {*}
 */
export function stringDivider(str, width, spaceReplacer) {
  if (str.length > width) {
    let p = width;
    while (p > 0 && (str[p] !== ' ' && str[p] !== '-')) {
      p--;
    }
    if (p > 0) {
      let left;
      if (str.substring(p, p + 1) === '-') {
        left = str.substring(0, p + 1);
      } else {
        left = str.substring(0, p);
      }
      let right = str.substring(p + 1);
      return left + spaceReplacer + stringDivider(right, width, spaceReplacer);
    }
  }
  return str;
}

/**
 * Word wrapping by length
 * @param {string} str The string to be word wrapped
 * @param {number} width The number of characters after which the string should be word wrapped
 * @return {string}
 */
export function wordWrap(str, width) {
  return str.replace(new RegExp(`(?![^\\n]{1,${width}}$)([^\\n]{1,${width}})\\s`, 'g'), '$1\n');
}

/**
 * Creates style objects for the booth dimensions rendered on the booth
 * @param {Object} feature
 * @param {Array<number>} [shortestSide=false] optional, defaults to false
 * @param {Array<number>} [longestSide=false] optional, defaults to false
 * @param {boolean} [selected=false] optional, defaults to false
 * @return {Object[]} Array with style objects
 */
export function createBoothDimensionText(feature, shortestSide = false, longestSide = false, selected = false) {
  let textDimensionRight;
  let textDimensionBottom;
  let fontSize = 10;

  const boothWidth = feature.get('width');
  const boothDepth = feature.get('depth');

  const topLeft = getTopLeftPoint(feature);
  const topRight = getTopRightPoint(feature);
  const bottomRight = getBottomRightPoint(feature);
  const bottomLeft = getBottomLeftPoint(feature);

  const top = topRight[0] - topLeft[0];
  const right = topRight[1] - bottomRight[1];

  const smallValue = parseFloat(boothWidth) < parseFloat(boothDepth) ? boothWidth : boothDepth;
  const highValue = parseFloat(boothWidth) > parseFloat(boothDepth) ? boothWidth : boothDepth;

  if (shortestSide !== false && longestSide !== false) {
    textDimensionRight = new Text({
      text: highValue + ' m',
      font: fontSize - 2 + 'px sans-serif',
      fill: new Fill({
        color: selected == true ? exhibitionConfig['styles']['booths']['number']['selected']['color'] : exhibitionConfig['styles']['booths']['number']['color'],
      }),
      placement: 'point',
      textAlign: 'center',
      textBaseline: 'middle',
      rotateWithView: true,
      overflow: false,
      padding: [10, 10, 10, 10],
      zIndex: 2,
    });

    textDimensionBottom = new Text({
      text: smallValue + ' m',
      font: fontSize - 2 + 'px sans-serif',
      fill: new Fill({
        color: selected == true ? exhibitionConfig['styles']['booths']['number']['selected']['color'] : exhibitionConfig['styles']['booths']['number']['color'],
      }),
      placement: 'point',
      textAlign: 'center',
      textBaseline: 'middle',
      rotateWithView: true,
      overflow: false,
      padding: [10, 10, 10, 10],
      zIndex: 2,
    });
  } else if (top < right) {
    textDimensionRight = new Text({
      text: highValue + ' m',
      font: fontSize - 2 + 'px sans-serif',
      fill: new Fill({
        color: selected == true ? exhibitionConfig['styles']['booths']['number']['selected']['color'] : exhibitionConfig['styles']['booths']['number']['color'],
      }),
      placement: 'point',
      textAlign: 'center',
      textBaseline: 'middle',
      rotation: 90 * (Math.PI / 180),
      rotateWithView: true,
      overflow: false,
      padding: [0, 0, 0, 0],
      zIndex: 2,
    });

    textDimensionBottom = new Text({
      text: smallValue + ' m',
      font: fontSize - 2 + 'px sans-serif',
      fill: new Fill({
        color: selected == true ? exhibitionConfig['styles']['booths']['number']['selected']['color'] : exhibitionConfig['styles']['booths']['number']['color'],
      }),
      placement: 'point',
      textAlign: 'center',
      textBaseline: 'middle',
      rotateWithView: true,
      overflow: false,
      padding: [0, 0, 10, 0],
      zIndex: 2,
    });
  } else {
    textDimensionRight = new Text({
      text: smallValue + ' m',
      font: fontSize - 2 + 'px sans-serif',
      fill: new Fill({
        color: selected == true ? exhibitionConfig['styles']['booths']['number']['selected']['color'] : exhibitionConfig['styles']['booths']['number']['color'],
      }),
      placement: 'point',
      textAlign: 'center',
      textBaseline: 'middle',
      rotation: 90 * (Math.PI / 180),
      rotateWithView: true,
      overflow: false,
      padding: [0, 0, 0, 0],
      zIndex: 2,
    });

    textDimensionBottom = new Text({
      text: highValue + ' m',
      font: fontSize - 2 + 'px sans-serif',
      fill: new Fill({
        color: selected == true ? exhibitionConfig['styles']['booths']['number']['selected']['color'] : exhibitionConfig['styles']['booths']['number']['color'],
      }),
      placement: 'point',
      textAlign: 'center',
      textBaseline: 'middle',
      rotateWithView: true,
      overflow: false,
      padding: [0, 0, 10, 0],
      zIndex: 2,
    });
  }

  let bottomLine = shortestSide !== false ? new LineString([shortestSide[0], shortestSide[1]]) : new LineString([bottomLeft, bottomRight]);
  let rightLine = longestSide !== false ? new LineString([longestSide[0], longestSide[1]]) : new LineString([topRight, bottomRight]);

  let dimensionWidthBottomPoint;
  let dimensionDepthRightPoint;

  dimensionWidthBottomPoint = shortestSide !== false ? createPoint(shortestSide[0], shortestSide[1]) : createPoint(bottomLine.getFirstCoordinate(), bottomLine.getLastCoordinate());
  dimensionDepthRightPoint = longestSide !== false ? createPoint(longestSide[0], longestSide[1]) : createPoint(rightLine.getFirstCoordinate(), rightLine.getLastCoordinate());
  let toCenterPoint1 = new LineString([getExtentCenter(feature.getGeometry().getExtent()), dimensionWidthBottomPoint.getCoordinates()]);
  let toCenterPoint2 = new LineString([getExtentCenter(feature.getGeometry().getExtent()), dimensionDepthRightPoint.getCoordinates()]);
  let point1 = toCenterPoint1.getCoordinateAt(0.7);
  let point2 = toCenterPoint2.getCoordinateAt(0.7);

  dimensionWidthBottomPoint = createPoint(point1, toCenterPoint1.getLastCoordinate());
  dimensionDepthRightPoint = createPoint(point2, toCenterPoint2.getLastCoordinate());

  let dimensionWidthBottomStyle = new Style({
    text: textDimensionBottom,
    geometry: dimensionWidthBottomPoint,
    zIndex: 2,
  });

  let dimensionDepthRightStyle = new Style({
    text: textDimensionRight,
    geometry: dimensionDepthRightPoint,
    zIndex: 2,
  });

  let bottomBoothNumberAngle = calculateAngleBetweenPoints(bottomLine);
  let rightBoothNumberAngle = calculateAngleBetweenPoints(rightLine);

  if (typeof bottomBoothNumberAngle !== 'undefined') {
    if ((bottomBoothNumberAngle * 180 / Math.PI) > 90 && (bottomBoothNumberAngle * 180 / Math.PI) < 180) {
      bottomBoothNumberAngle = ((bottomBoothNumberAngle * 180 / Math.PI) + 180) * Math.PI / 180;
    }

    dimensionWidthBottomStyle.getText().setRotation(-bottomBoothNumberAngle);
  }

  if (typeof rightBoothNumberAngle !== 'undefined') {
    if ((rightBoothNumberAngle * 180 / Math.PI) > 90 && (rightBoothNumberAngle * 180 / Math.PI) < 180) {
      rightBoothNumberAngle = ((rightBoothNumberAngle * 180 / Math.PI) + 180) * Math.PI / 180;
    }
    dimensionDepthRightStyle.getText().setRotation(-rightBoothNumberAngle);
  }

  let newRotation;
  let viewRotationInDegree = map.getView().getRotation() * (180 / Math.PI);

  if (dimensionDepthRightStyle.getText().getRotation() * (180 / Math.PI) + viewRotationInDegree > 90 && dimensionDepthRightStyle.getText().getRotation() * (180 / Math.PI) + viewRotationInDegree < 270) {
    newRotation = (dimensionDepthRightStyle.getText().getRotation() * (180 / Math.PI) + 180) * (Math.PI / 180);
    dimensionDepthRightStyle.getText().setRotation(newRotation);
  }

  if (dimensionWidthBottomStyle.getText().getRotation() * (180 / Math.PI) + viewRotationInDegree > 90 && dimensionWidthBottomStyle.getText().getRotation() * (180 / Math.PI) + viewRotationInDegree < 270) {
    newRotation = (dimensionWidthBottomStyle.getText().getRotation() * (180 / Math.PI) + 180) * (Math.PI / 180);
    dimensionWidthBottomStyle.getText().setRotation(newRotation);
  }

  return [dimensionWidthBottomStyle, dimensionDepthRightStyle];
}

/**
 * Create a point by two given line coordinates
 * @param {array} pointA First point coordinate (x/y)
 * @param {array} pointB Second point coordinate (x/y)
 * @return {object}
 */
function createPoint(pointA, pointB) {
  let line = getX(pointA) < getX(pointB) || getY(pointA) < getY(pointB) ? new LineString([pointA, pointB]) : new LineString([pointB, pointA]);

  return new Point(getExtentCenter(line.getExtent()));
}

/**
 * Create bootnumber text point
 * @param {object} feature
 * @param {number} featureNumber
 * @param {number} fontSize
 * @param {array} featureStyle
 * @param {object} selected
 *
 * @return {object}
 */
export function createBoothNumberTextPoint(feature, featureNumber, fontSize, featureStyle, selected = false) {
  let numberObjectOffsetX;
  let numberObjectOffsetY;
  let textAlign;
  let numberObject;
  let line;

  if (feature.getGeometry().getCoordinates(false)[0].length == 4) { // Dreieck
    line = new LineString(compareCoordinates(feature.getGeometry().getCoordinates(false)[0]));
  } else if (feature.getGeometry().getCoordinates(false)[0].length == 5) { // Polygon mit 4 Punkten
    if (typeof featureStyle['number']['position'] !== 'undefined' && featureStyle['number']['position'] !== null) {
      line = getStandnummerLine(feature, featureStyle['number']['position']);
    } else {
      line = getStandnummerLine(feature);
    }
  } else if (feature.getGeometry().getCoordinates(false)[0].length > 5) { // Polygon mit >= 5 Punkten
    if (typeof featureStyle['number']['position'] !== 'undefined' && featureStyle['number']['position'] !== null) {
      line = getStandnummerLine(feature, featureStyle['number']['position']);
    } else {
      line = getStandnummerLine(feature);
    }
  }

  if (typeof line === 'undefined') {
    return new Style({});
  }
  let point = new Point(line.getFirstCoordinate());

  if (typeof featureStyle['number']['position'] !== 'undefined' && featureStyle['number']['position'] !== null) {
    switch (featureStyle['number']['position']) {
      case 'tl2tr': // Top left to top right
      numberObjectOffsetX = 8.5;
      numberObjectOffsetY = 8.5;
      textAlign = 'left';
        break;
      case 'tr2br': // Top right to bottom right
        numberObjectOffsetX = 8.5;
        numberObjectOffsetY = 8.5;
        textAlign = 'top';
        break;
      case 'bl2br': // Bottom left to bottom right
        numberObjectOffsetX = 8.5;
        numberObjectOffsetY = -8.5;
        textAlign = 'left';
        break;
      case 'bl2tl': // Bottom right to top right
        numberObjectOffsetX = 8.5;
        numberObjectOffsetY = 8.5;
        textAlign = 'left';
        break;
      default: // Defaults to tl2tr - Top left to top right
        numberObjectOffsetX = 8.5;
        numberObjectOffsetY = 8.5;
        textAlign = 'left';
        break;
    }
  } else {
    numberObjectOffsetX = 8.5;
    numberObjectOffsetY = 8.5;
    textAlign = 'left';
  }

  numberObject = new Style({
    text: new Text({
      text: featureNumber !== null ? ' ' + featureNumber : '',
      font: fontSize - 1 + 'px sans-serif',
      fill: new Fill({
        color: selected == true ? featureStyle['number']['selected']['color'] : featureStyle['number']['color'],
      }),
      placement: 'point',
      textAlign: textAlign,
      textBaseline: 'middle',
      rotateWithView: true,
      overflow: false,
      offsetX: numberObjectOffsetX,
      offsetY: numberObjectOffsetY,
    }),
    geometry: point,
    zIndex: 1,
  });

  if (typeof exhibitionConfig['core']['fairground'] !== 'undefined' && exhibitionConfig['core']['fairground'] == 'koeln') {
    numberObject.getText().setFont('bold ' + fontSize + 'px sans-serif');
  }

  let boothNumberAngle = calculateAngleBetweenPoints(line);

  if (typeof boothNumberAngle !== 'undefined') {
    boothNumberAngle = -boothNumberAngle;
    numberObject.getText().setRotation(boothNumberAngle);
  }

  return numberObject;
}

/**
 * Calculate the angle of a line between its start point and its end point
 * @param {object} lineString
 * @return {number}
 */
function calculateAngleBetweenPoints(lineString) {
  let x1 = getX(lineString.getFirstCoordinate());
  let y1 = getY(lineString.getFirstCoordinate());
  let x2 = getX(lineString.getLastCoordinate());
  let y2 = getY(lineString.getLastCoordinate());

  return Math.atan2(y2 - y1, x2 - x1);
}

/**
 * Function compares x and y coordinates to determine the right angle of a triangle
 * @param {*} coordinates
 * @return {array}
 */
function compareCoordinates(coordinates) {
  let pointA = coordinates[0];
  let pointB = coordinates[1];
  let pointC = coordinates[2];

  let ab = getLength(pointA, pointB);
  let ac = getLength(pointA, pointC);
  let bc = getLength(pointA, pointB);

  /**
  * alpha = Innenwinkel Punkt A
  * beta = Innenwinkel Punkt B
  * gamma = Innenwinwinkel Punkt C
  */

  let alpha = getAlpha(ab, ac, bc);
  let beta = getBeta(ab, ac, bc);
  let gamma = getGamma(ab, ac, bc);

  /**
   * Größter Innenwinkel bestimmen und dann richtige Reihenfolge der beiden Punkte bestimmen.
   */
  if (alpha > beta && alpha > gamma) {
      if (ab > ac) return [pointA, pointB];
      return [pointA, pointC];
  } else if (beta > alpha && beta > gamma) {
      if (ab > bc) return [pointB, pointA];
      return [pointB, pointC];
  } else {
      if (bc > ac) return [pointC, pointB];
      return [pointC, pointA];
  }
}

/**
 * Get length between two (2) points
 * @param {*} pointA
 * @param {*} pointB
 * @return {number}
 */
function getLength(pointA, pointB) {
  let x = getX(pointA) - getX(pointB);
  if (x < 0) x *= (-1);
  let y = getY(pointA) - getY(pointB);
  if (y < 0) y *= (-1);

  return Math.sqrt((Math.pow(x, 2) + (Math.pow(y, 2))));
}

/**
 * Get alpha
 * @param {*} ab
 * @param {*} ac
 * @param {*} bc
 * @return {number}
 */
function getAlpha(ab, ac, bc) {
  let cosinusAlpha = (Math.pow(bc, 2) - Math.pow(ac, 2) - Math.pow(ab, 2)) / (-2 * ac * ab);
  return Math.acos(cosinusAlpha);
}

/**
 * Get beta
 * @param {*} ab
 * @param {*} ac
 * @param {*} bc
 * @return {number}
 */
function getBeta(ab, ac, bc) {
  let cosinusBeta = (Math.pow(ac, 2) - Math.pow(bc, 2) - Math.pow(ab, 2)) / (-2 * bc * ab);
  return Math.acos(cosinusBeta);
}

/**
 * Get gamma
 * @param {*} ab
 * @param {*} ac
 * @param {*} bc
 * @return {number}
 */
function getGamma(ab, ac, bc) {
  let cosinusGamma = (Math.pow(ab, 2) - Math.pow(bc, 2) - Math.pow(ac, 2)) / (-2 * bc * ac);
  return Math.acos(cosinusGamma);
}

/**
 * Gets the x coordinate out of an array with x/y coordinates
 * @param {array} coordinates An array with x and y coordinates
 * @return {number}
 */
export function getX(coordinates) {
  return coordinates[0];
}

/**
 * Gets the y coordinate out of an array with x/y coordinates
 * @param {array} coordinates An array with x and y coordinates
 * @return {number}
 */
export function getY(coordinates) {
  return coordinates[1];
}

/**
 * @param {array} coordinates
 * @return {number}
 */
function getLowestY(coordinates) {
  if (getY(coordinates[0]) < getY(coordinates[1]) && getY(coordinates[0]) < getY(coordinates[2]) && getY(coordinates[0]) < getY(coordinates[3])) {
    return getY(coordinates[0]);
  } else if (getY(coordinates[1]) < getY(coordinates[0]) && getY(coordinates[1]) < getY(coordinates[2]) && getY(coordinates[1]) < getY(coordinates[3])) {
    return getY(coordinates[1]);
  } else if (getY(coordinates[2]) < getY(coordinates[0]) && getY(coordinates[2]) < getY(coordinates[1]) && getY(coordinates[2]) < getY(coordinates[3])) {
    return getY(coordinates[2]);
  } else if (getY(coordinates[3]) < getY(coordinates[0]) && getY(coordinates[3]) < getY(coordinates[1]) && getY(coordinates[3]) < getY(coordinates[2])) {
    return getY(coordinates[3]);
  }
}

/**
 * Defines the style configuration to be used for the feature
 * @param {object} feature
 * @return {*}
 */
export function defineFeatureStyle(feature) {
  if (feature.get('type') === 'Halle') {
    return exhibitionConfig['styles']['halls'];
  } else if (feature.get('type') === 'Hallenkomponente') {
    return exhibitionConfig['styles']['hallComponent'];
  } else if (feature.get('type') === 'Stand') {
    return exhibitionConfig['styles']['booths'];
  } else if (feature.get('type') === 'Eingang') {
    return exhibitionConfig['styles']['entries'];
  } else if (feature.get('type') === 'Freigelände') {
    return exhibitionConfig['styles']['openAirGrounds'];
  } else if (feature.get('type') === 'Hof') {
    return exhibitionConfig['styles']['yards'];
  } else if (feature.get('type') === 'Hofkomponente') {
    return exhibitionConfig['styles']['yardsComponent'];
  } else if (feature.get('type') === 'ICM') {
    return exhibitionConfig['styles']['icm'];
  } else if (feature.get('type') === 'Atrium') {
    return exhibitionConfig['styles']['atrium'];
  } else if (feature.get('type') === 'Freigeländekomponente') {
    return exhibitionConfig['styles']['freeAreaComponent'];
  } else if (feature.get('type') === 'Grünfläche') {
    return exhibitionConfig['styles']['greenArea'];
  } else if (feature.get('type') === 'Marker') {
    return {};
  } else if (feature.get('type') === 'TextPoint') {
    return {};
  } else {
    return feature.get('style');
  }
}

/**
 * Gets the line for the Standnummer
 * @param {object} feature
 * @param {?string} position
 *
 * @return {object}
 */
export function getStandnummerLine(feature, position = 'tl2tr') {
  let startPoint = null;
  let endPoint = null;
  let lastPoint = null;

  switch (position) {
    case 'tl2tr': // Top left to top right
      // startPoint = getPoint(feature, 'topLeft');
      // endPoint = getPoint(feature, 'topRight');
      startPoint = getTopLeftPoint(feature);
      endPoint = getTopRightPoint(feature);
      lastPoint = getBottomLeftPoint(feature);
      break;
    case 'tr2br': // Top right to bottom right
      // startPoint = getPoint(feature, 'topRight');
      // endPoint = getPoint(feature, 'bottomRight');
      startPoint = getTopRightPoint(feature);
      endPoint = getBottomRightPoint(feature);
      lastPoint = getTopLeftPoint(feature);
      break;
    case 'bl2br': // Bottom left to bottom right
      // startPoint = getPoint(feature, 'bottomLeft');
      // endPoint = getPoint(feature, 'bottomRight');
      startPoint = getBottomLeftPoint(feature);
      endPoint = getBottomRightPoint(feature);
      lastPoint = getTopRightPoint(feature);
      break;
    case 'bl2tl': // Bottom right to top right
      // startPoint = getPoint(feature, 'bottomLeft');
      // endPoint = getPoint(feature, 'topLeft');
      startPoint = getBottomLeftPoint(feature);
      endPoint = getTopLeftPoint(feature);
      lastPoint = getPreviewPoint(feature, startPoint);
      break;
    default:
      // startPoint = getPoint(feature, 'bottomLeft');
      // endPoint = getPoint(feature, 'topLeft');
      startPoint = getTopLeftPoint(feature);
      endPoint = getTopRightPoint(feature);
      lastPoint = getBottomLeftPoint(feature);
      break;
  }

  let ab = getLength(lastPoint, startPoint);
  let ac = getLength(lastPoint, endPoint);
  let bc = getLength(startPoint, endPoint);
  let beta = getBeta(ab, ac, bc);

  if (beta * 180 / Math.PI < 80) {
    startPoint = getNextPoint(feature, startPoint, 1);
    endPoint = getNextPoint(feature, startPoint, 2);
  }

  return new LineString([startPoint, endPoint]);
}

/**
 * Get the specified point of the feature
 * @param {object} feature
 * @param {string} pointPosition
 *
 * @return {array}
 * TODO: Eingestampfte Funktion soll die anderen ersetzen. Aber hier wird noch ein Fehler verursacht
 */
// function getPoint(feature, pointPosition) {
//   let coordinates = feature.getGeometry().getCoordinates()[0];
//   let calculatedPoint = coordinates[0];
//   let pointOfExtent = null;
//   let length = 0;
//   let tempLength = 0;

//   switch (pointPosition) {
//     case 'topLeft':
//       pointOfExtent = getTopLeft(feature.getGeometry().getExtent());
//       break;
//     case 'topRight':
//       pointOfExtent = getTopRight(feature.getGeometry().getExtent());
//       break;
//     case 'bottomLeft':
//       pointOfExtent = getBottomLeft(feature.getGeometry().getExtent());
//       break;
//     case 'bottomRight':
//       pointOfExtent = getBottomRight(feature.getGeometry().getExtent());
//       break;
//     default:
//       pointOfExtent = getTopLeft(feature.getGeometry().getExtent());
//       break;
//   }

//   length = getLength(pointOfExtent, calculatedPoint);

//   for (let i = 1; i < coordinates.length; i++) {
//     tempLength = getLength(pointOfExtent, coordinates[i]);

//     if (tempLength < length) {
//       calculatedPoint = coordinates[i];
//       length = tempLength;
//       return;
//     }

//     if (pointPosition == 'topLeft' || pointPosition == 'bottomLeft') {
//       if (tempLength == length && getY(calculatedPoint) < getY(coordinates[i])) {
//         calculatedPoint = coordinates[i];
//       }
//     } else if (pointPosition == 'topRight' || pointPosition == 'bottomRight') {
//       if (tempLength == length && getX(calculatedPoint) < getX(coordinates[i])) {
//         calculatedPoint = coordinates[i];
//       }
//     }
//   }

//   return calculatedPoint;
// }
/**
 * Gets the top left point of a feature (lowest x, highest y)
 * @param {object} feature
 *
 * @return {array}
 */
function getTopLeftPoint(feature) {
  let coordinates = feature.getGeometry().getCoordinates(false)[0];
  let topLeftOfExtent = getTopLeft(feature.getGeometry().getExtent());
  let calculatedTopLeft = coordinates[0];
  let length = getLength(topLeftOfExtent, calculatedTopLeft);

  coordinates.slice(1).forEach((point) => {
    let tempLength = getLength(topLeftOfExtent, point);

    if (tempLength < length) {
      calculatedTopLeft = point;
      length = tempLength;
      return;
    }

    if (tempLength == length && getY(calculatedTopLeft) < getY(point)) {
      calculatedTopLeft = point;
    }
  });

  return calculatedTopLeft;
}
/**
 * Gets the top right point of a feature (highest x, highest y)
 * @param {object} feature
 *
 * @return {array}
 */
function getTopRightPoint(feature) {
  let coordinates = feature.getGeometry().getCoordinates(false)[0];
  let topRightOfExtent = getTopRight(feature.getGeometry().getExtent());
  let calculatedTopRight = coordinates[0];
  let length = getLength(topRightOfExtent, calculatedTopRight);

  coordinates.slice(1).forEach((point) => {
    let tempLength = getLength(topRightOfExtent, point);

    if (tempLength < length) {
      calculatedTopRight = point;
      length = tempLength;
      return;
    }

    if (tempLength == length && getX(calculatedTopRight) < getX(point)) {
      calculatedTopRight = point;
    }
  });

  return calculatedTopRight;
}
/**
 * Gets the bottom left point of a feature (lowest x, lowest y)
 * @param {object} feature
 *
 * @return {array}
 */
function getBottomLeftPoint(feature) {
  let coordinates = feature.getGeometry().getCoordinates(false)[0];
  let bottomLeftOfExtent = getBottomLeft(feature.getGeometry().getExtent());
  let calculatedBottomLeft = coordinates[0];
  let length = getLength(bottomLeftOfExtent, calculatedBottomLeft);

  coordinates.slice(1).forEach((point) => {
    let tempLength = getLength(bottomLeftOfExtent, point);

    if (tempLength < length) {
      calculatedBottomLeft = point;
      length = tempLength;
      return;
    }

    if (tempLength == length && getY(calculatedBottomLeft) < getY(point)) {
      calculatedBottomLeft = point;
    }
  });

  return calculatedBottomLeft;
}
/**
 * Gets the bottom right point of a feature (highest x, lowest y)
 * @param {object} feature
 *
 * @return {array}
 */
function getBottomRightPoint(feature) {
  let coordinates = feature.getGeometry().getCoordinates(false)[0];
  let bottomRightOfExtent = getBottomRight(feature.getGeometry().getExtent());
  let calculatedBottomRight = coordinates[0];
  let length = getLength(bottomRightOfExtent, calculatedBottomRight);

  coordinates.slice(1).forEach((point) => {
    let tempLength = getLength(bottomRightOfExtent, point);

    if (tempLength < length) {
      calculatedBottomRight = point;
      length = tempLength;
      return;
    }

    if (tempLength == length && getX(calculatedBottomRight) < getX(point)) {
      calculatedBottomRight = point;
    }
  });

  return calculatedBottomRight;
}

/**
 * Get point before start point
 * @param {object} feature
 * @param {array} startPoint
 * @return {array}
 */
function getPreviewPoint(feature, startPoint) {
  let coordinates = feature.getGeometry().getCoordinates(false)[0];
  let key = 0;

  for (let i = 0; i < coordinates.length; i++) {
    if (coordinates[i] == startPoint) {
      key = i-1;
    }
  }

  return coordinates[key];
}

/**
 * Get the next point from given starting point
 * @param {object} feature
 * @param {array} startPoint
 * @param {number} offset
 * @return {array}
 */
function getNextPoint(feature, startPoint, offset = 1) {
  let coordinates = feature.getGeometry().getCoordinates(false)[0];
  let key = 0;

  for (let i = 0; i < coordinates.length; i++) {
    if (getX(coordinates[i]) == getX(startPoint) && getY(coordinates[i]) == getY(startPoint)) {
      if (i + parseInt(offset) <= coordinates.length - 1) {
        key = i + parseInt(offset);
      } else {
        let difference = (i + parseInt(offset)) - (coordinates.length - 1);
        key = difference - 1 >= 0 ? difference - 1 : difference;
      }
    }
  }

  return coordinates[key];
}

/**
 * Calculate the shortest and the longest polygon side
 * @param {object} feature
 * @return {array}
 */
export function calculateSidesForBoothDimension(feature) {
  let shortestSideLine = undefined;
  let longestSideLine = undefined;
  // let longestSide = getLength(feature.getGeometry().getCoordinates()[0]);
  if (feature.getGeometry().getCoordinates(false)[0].length == 4) {
    // Dreieck
    let biggestAngleCoordinates = compareCoordinates(feature.getGeometry().getCoordinates(false)[0]);
    let line1Length = getLength(feature.getGeometry().getCoordinates(false)[0][2], biggestAngleCoordinates[0]);
    let line2Length = getLength(biggestAngleCoordinates[0], feature.getGeometry().getCoordinates(false)[0][1]);

    let lastKey = 0;
    let nextKey = 2;

    for (const [key, value] of Object.entries(feature.getGeometry().getCoordinates(false)[0])) {
      if (getX(value) == getX(biggestAngleCoordinates[0]) && getY(value) == getY(biggestAngleCoordinates[0])) {
        if (key == feature.getGeometry().getCoordinates(false)[0].length - 1) {
          lastKey = parseInt(key)-1;
          nextKey = 0;
        } else if (key == 0) {
          lastKey = feature.getGeometry().getCoordinates(false)[0].length - 1;
          nextKey = parseInt(key)+1;
        } else {
          lastKey = parseInt(key)-1;
          nextKey = parseInt(key)+1;
        }

        line1Length = getLength(feature.getGeometry().getCoordinates(false)[0][lastKey], biggestAngleCoordinates[0]);
        line2Length = getLength(biggestAngleCoordinates[0], feature.getGeometry().getCoordinates(false)[0][nextKey]);
      }
    }

    if (line1Length < line2Length) {
      shortestSideLine = [feature.getGeometry().getCoordinates(false)[0][lastKey], biggestAngleCoordinates[0]];
      longestSideLine = [biggestAngleCoordinates[0], feature.getGeometry().getCoordinates(false)[0][nextKey]];
    } else if (line1Length > line2Length) {
      shortestSideLine = [biggestAngleCoordinates[0], feature.getGeometry().getCoordinates(false)[0][nextKey]];
      longestSideLine = [feature.getGeometry().getCoordinates(false)[0][lastKey], biggestAngleCoordinates[0]];
    } else if (line1Length == line2Length) {
      shortestSideLine = [feature.getGeometry().getCoordinates(false)[0][lastKey], biggestAngleCoordinates[0]];
      longestSideLine = [biggestAngleCoordinates[0], feature.getGeometry().getCoordinates(false)[0][nextKey]];
    }
  } else {
    if (feature.getGeometry().getCoordinates(false)[0].length == 5) {
      let coordinates = feature.getGeometry().getCoordinates(false)[0];
      let shortestSide = getLength(coordinates[0], coordinates[1]);
      let longestSide = getLength(coordinates[1], coordinates[2]);

      let nextKey = 1;

      for (const [key] of Object.entries(coordinates)) {
        if (key == coordinates.length - 1) {
          nextKey = 0;
        } else {
          nextKey = parseInt(key) + 1;
        }

        if (key == nextKey || key == coordinates.length - 1) {
          continue;
        }

        if (getLength(coordinates[key], coordinates[nextKey]) < shortestSide) {
          shortestSideLine = [coordinates[key], coordinates[nextKey]];
          shortestSide = getLength(coordinates[key], coordinates[nextKey]);
        } else if (getLength(coordinates[key], coordinates[nextKey]) > longestSide) {
          longestSideLine = [coordinates[key], coordinates[nextKey]];
          longestSide = getLength(coordinates[key], coordinates[nextKey]);
        }
      }
    }
    // Polygon mit >= 4 Seiten
  }

  if (shortestSideLine != undefined && longestSideLine != undefined) {
    return [shortestSideLine, longestSideLine];
  } else {
    return undefined;
  }
}


/**
 * foo
 * @param {object} feature
 * @param {Boolean} rotate
 * @return {Polygon}
 */
export function koelnPseudoPolygon(feature, rotate) {
  if (typeof feature.get('pseudoPolygon') !== 'undefined') {
    return feature.get('pseudoPolygon');
  } else {
    let pseudoPolygon;

    if (feature.getGeometry().getCoordinates(false)[0].length == 4) {
      let pointA = feature.getGeometry().getCoordinates(false)[0][0];
      let pointB = feature.getGeometry().getCoordinates(false)[0][1];
      let pointC = feature.getGeometry().getCoordinates(false)[0][2];
      pseudoPolygon = new Circle(getTriangleInnerCircleCenterCoordinates(pointA, pointB, pointC), getTriangleInnerCircleRadius(pointA, pointB, pointC));
    } else if (feature.getGeometry().getCoordinates(false)[0].length == 5) {
      let polygonCoords = feature.getGeometry().getCoordinates(false)[0];

      if (isRectangle(polygonCoords[0], polygonCoords[1], polygonCoords[2], polygonCoords[3], feature)) {
        pseudoPolygon = new Circle(getExtentCenter(feature.getGeometry().getExtent()), getRectangleRadius(polygonCoords[0], polygonCoords[1], polygonCoords[2]));
      } else {
        let polygonInnerCircle = getPolygonInnerCircle(polygonCoords);
        pseudoPolygon = new Circle([polygonInnerCircle[0], polygonInnerCircle[1]], polygonInnerCircle[2]);
      }
    } else {
      let polygonInnerCircle = getPolygonInnerCircle(feature.getGeometry().getCoordinates(false)[0]);
      pseudoPolygon = new Circle([polygonInnerCircle[0], polygonInnerCircle[1]], polygonInnerCircle[2]);
    }

    pseudoPolygon = fromExtent(pseudoPolygon.getExtent());
    feature.set('pseudoPolygon', pseudoPolygon);
    pseudoPolygon.rotate(rotate, getExtentCenter(pseudoPolygon.getExtent()));

    return pseudoPolygon;
  }
}

/**
 * Get the center point of a triangle
 * @param {number[]} pointA
 * @param {number[]} pointB
 * @param {number[]} pointC
 * @return {number[]}
 */
function getTriangleInnerCircleCenterCoordinates(pointA, pointB, pointC) {
  let xa = pointA[0];
  let ya = pointA[1];
  let xb = pointB[0];
  let yb = pointB[1];
  let xc = pointC[0];
  let yc = pointC[1];

  let sideAB = getLength(pointA, pointB);
  let sideAC = getLength(pointA, pointC);
  let sideBC = getLength(pointB, pointC);

  let centerX = (sideBC * xa + sideAC * xb + sideAB * xc) / (sideAB + sideAC + sideBC);
  let centerY = (sideBC * ya + sideAC * yb + sideAB * yc) / (sideAB + sideAC + sideBC);

  return [centerX, centerY];
}

/**
 * Get the radius of a circle in a triangle
 * @param {number[]} pointA
 * @param {number[]} pointB
 * @param {number[]} pointC
 * @return {number}
 */
function getTriangleInnerCircleRadius(pointA, pointB, pointC) {
  let sideAB = getLength(pointA, pointB);
  let sideAC = getLength(pointA, pointC);
  let sideBC = getLength(pointB, pointC);

  let s = (sideAB + sideAC + sideBC)/2;

  return Math.sqrt(((s-sideAB) * (s-sideAC) * (s-sideBC))/s);
}

/**
 * Get the radius of a circle in a polygon
 * @param {number[]} coordinates
 * @return {number[]}
 */
function getPolygonInnerCircle(coordinates) {
  let center = [[0], [0]];
  let radius = 0;
  let triplets = [];
  let counter = 0;

  for (let i = 0; i < coordinates.length - 2; i++) {
    for (let j = i + 1; j < coordinates.length - 1; j++) {
      for (let k = j + 1; k < coordinates.length; k++) {
        triplets[counter] = [coordinates[i], coordinates[j], coordinates[k]];
        counter++;
      }
    }
  }

  for (let i = 0; i < triplets.length; i++) {
    let newR = getTriangleInnerCircleRadius(
      triplets[i][0],
      triplets[i][1],
      triplets[i][2]
    );
    if (newR > radius) {
      radius = newR;
      center = getTriangleInnerCircleCenterCoordinates(
        triplets[i][0],
        triplets[i][1],
        triplets[i][2]
      );
    }
  }

  return [center[0], center[1], radius];
}

/**
 * Check if polygon is rectangle
 * @param {number[]} pointA
 * @param {number[]} pointB
 * @param {number[]} pointC
 * @param {number[]} pointD
 * @return {Boolean}
 */
function isRectangle(pointA, pointB, pointC, pointD) {
  let sideAB = Math.round((getLength(pointA, pointB) + Number.EPSILON) * 1000) / 1000;
  let sideBC = Math.round((getLength(pointB, pointC) + Number.EPSILON) * 1000) / 1000;
  let sideCD = Math.round((getLength(pointC, pointD) + Number.EPSILON) * 1000) / 1000;
  let sideDA = Math.round((getLength(pointD, pointA) + Number.EPSILON) * 1000) / 1000;

  if (sideAB == sideBC == sideCD == sideDA) return true;
  if (sideAB == sideCD && sideBC == sideDA) {
      return Math.sqrt((Math.pow(sideAB, 2) + Math.pow(sideDA, 2))) == Math.sqrt((Math.pow(sideBC, 2) + Math.pow(sideCD, 2)));
  }
  return false;
}

/**
 * Get circle of rectangle
 * @param {number[]} pointA
 * @param {number[]} pointB
 * @param {number[]} pointC
 * @return {number}
 */
function getRectangleRadius(pointA, pointB, pointC) {
  let sideAB = getLength(pointA, pointB);
  let sideBC = getLength(pointB, pointC);
  if (sideAB < sideBC) return sideAB/2;
  return sideBC/2;
}

export function createCenteredTextLayer(center, label, opts = {}) {
  const featureStyle = exhibitionConfig['styles']['booths'];
  const {
    baseFontSize = 20,
    minFontSize = 16,
    maxFontSize = 82,
    fontFamily = 'Arial,sans-serif',
    textColor = 'rgba(0,0,0,0.9)',
    backgroundColor = 'rgba(255,255,255,0.0)',
    padding = [4, 6, 4, 6],
    zIndex = 1000,
  } = opts;

  const feature = new Feature({ geometry: new Point(center) });

  const text = new Text({
    text: label ?? '',
    textAlign: 'center',
    textBaseline: 'middle',
    fill: new Fill({ color: textColor }),
    backgroundFill: backgroundColor ? new Fill({ color: backgroundColor }) : undefined,
    padding,
  });

  const style = new Style({ text });

  const styleFn = (_, resolution) => {
    let px = 0;

    if (resolution < 0.8) {
      px = Math.max(minFontSize, Math.min(maxFontSize, baseFontSize * (1 / resolution)));
    } else {
      px = 0;
    }

    text.setFont(`${Math.round(px)}px ${fontFamily}`);

    return style;
  }

  return new VectorLayer({
    source: new VectorSource({ features: [feature] }),
    style: styleFn,
    zIndex,
  })
}

export function fitText(feature, text, resolution) {
  if (text == 'HANVAC') {
    console.debug('fitText wird aufgerufen...');
    console.debug('Feature:', feature);
    console.debug('Text:', text);
    console.debug('Resolution:', resolution);
  }
  let fontSizePerPx = 0.6;

  let extent = feature.getGeometry().getExtent();
  let widthPx = (extent[2] - extent[0]) / resolution;
  let heightPx = (extent[3] - extent[1]) / resolution;

  let textPadding = 80;
  let minTextPx = 20;
  let availablePx = Math.max(minTextPx, Math.min(widthPx, heightPx) - textPadding);
  let pxPerChar = fontSize * fontSizePerPx;
  let maxChars = Math.floor(availablePx, pxPerChar);
  let fittedText = text;

  //hier eig loop machen?
  let longestLine = text.split("\n").reduce((a, b) => a.length > b.length ? a : b);
  let lineLength = longestLine.length;

  if (text == 'HANVAC') {
    console.debug('DEBUG extent:', extent);
    console.debug('DEBUG widthPx:', widthPx);
    console.debug('DEBUG heightPx:', heightPx);
    console.debug('DEBUG availablePx:', availablePx);
    console.debug('DEBUG pxPerChar:', pxPerChar);
    console.debug('DEBUG maxChars:', maxChars);
    console.debug('DEBUG longestLine:', longestLine);
    console.debug('DEBUG lineLength:', lineLength);
  }


  if (lineLength > maxChars)
  {
    if (text == 'HANVAC') {
      console.debug('INFO: lineLength > maxChars ist true');
    }
    let minFontSize = 7;
    while (fontSize > minFontSize && (lineLength * (fontSize * fontSizePerPx)) > availablePx)
    {
      fontSize--;
    }

    if (text == 'HANVAC') {
      console.debug('DEBUG fontSize:', fontSize);
    }

    maxChars = Math.floor(availablePx / (fontSize * fontSizePerPx));

    if (maxChars < longestLine.length)
    {
      const ellipsisPx = pxPerChar;
      const charBudget = availablePx - ellipsisPx;

      let allowedChars = Math.floor(charBudget / pxPerChar);

      if (allowedChars < 1)
      {
        allowedChars = 1; // mindestens 1 Zeichen
      }

      fittedText = longestLine.substring(0, Math.max(1, maxChars - 1)) + "…";
    }
  }

  if (text == 'HANVAC') {
    console.debug('Fitted Text:', fittedText);
  }

  return fittedText;
}